import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, mergeMap } from 'rxjs/operators';
import { IDENTIFIER_REQUIRED } from '../../constants/multi-factor-authentication/multi-factor-authentication-constant';
import {
  AuthAccessTokenDetails,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  LoginOAuthResponse,
  SwapAuthForAccessTokens
} from '../../models/login';
import {
  isCompletedStatus,
  isUserNamePasswordRequired,
  returnAuthorizationResponseCode
} from '../../utils/login';
import { LoginPasswordlessFacadeHelperService } from './login-passwordless-facade-helper.service';

@Injectable({
  providedIn: 'root'
})
export class LoginPasswordlessFacadeService extends LoginPasswordlessFacadeHelperService {
  initiatePasswordlessLogin(
    loginOAuthParams: LoginOAuthParams,
    eamAuthStateValue?: string
  ): Observable<AuthAccessTokenDetails | string> {
    return this.loginAuthorization
      .loginAuthorization(loginOAuthParams, eamAuthStateValue)
      .pipe(
        concatMap((response: LoginOAuthResponse) => {
          if (
            response.status === loginOAuthParams.oauthConfig.IS_USER_REMEMBERED
          ) {
            return this.loginPostWithFingerPrint.loginPostWithFingerPrint(
              loginOAuthParams.oauthConfig,
              response?.id ? response.id : ''
            );
          } else if (
            response.status === loginOAuthParams.oauthConfig.COMPLETED
          ) {
            return this.swapAccessToken.swapAuthCodeForToken(
              loginOAuthParams,
              response.authorizeResponse?.code
            );
          } else {
            return throwError(response);
          }
        }),
        concatMap(
          (
            response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens
          ) => {
            if (isUserNamePasswordRequired(response, loginOAuthParams)) {
              return of(response);
            } else if ('access_token' in response) {
              return of(response);
            } else if (isCompletedStatus(response, loginOAuthParams)) {
              return this.swapAccessToken.swapAuthCodeForToken(
                loginOAuthParams,
                returnAuthorizationResponseCode(response)
              );
            } else {
              return throwError(response);
            }
          }
        ),
        concatMap(
          (
            response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens
          ) => {
            return this.processResponse(response, loginOAuthParams);
          }
        ),
        concatMap(
          (
            response: LoginAuthWithCredentialsResponse | AuthAccessTokenDetails
          ) => {
            if ('access_token' in response) {
              return of(response);
            } else if (isUserNamePasswordRequired(response, loginOAuthParams)) {
              return this.loginAlternativeAuthentication.loginAlternativeAuthentication(
                loginOAuthParams.oauthConfig,
                response?.id ? response.id : ''
              );
            } else {
              return throwError(response);
            }
          }
        ),
        concatMap(
          (
            response: LoginAuthWithCredentialsResponse | AuthAccessTokenDetails
          ) => {
            if ('access_token' in response) {
              return of(response);
            } else if (response.status === IDENTIFIER_REQUIRED) {
              return this.handleIdentifierRequired(response, loginOAuthParams);
            } else {
              return throwError(response);
            }
          }
        ),

        catchError((error) => {
          if (typeof error === 'string') {
            return of(error);
          } else {
            return timer(
              loginOAuthParams.oauthConfig.LOGIN_RETRY_TIMER
                ? loginOAuthParams.oauthConfig.LOGIN_RETRY_TIMER
                : 1000
            ).pipe(
              mergeMap(() => {
                this.retryCount = this.retryCount + 1;
                if (
                  this.retryCount !==
                  loginOAuthParams.oauthConfig.LOGIN_RETRY_COUNT
                    ? loginOAuthParams.oauthConfig.LOGIN_RETRY_COUNT
                    : 0
                ) {
                  loginOAuthParams.pkceCode =
                    this.loginRememberMeUtils.generateCodeChallenge();
                  return this.initiatePasswordlessLogin(loginOAuthParams);
                } else {
                  this.retryCount = 0;
                  return of(
                    this.loginRememberMeErrorUtil.checkRememberMeErrors(
                      loginOAuthParams.oauthConfig,
                      error
                    )
                  );
                }
              })
            );
          }
        })
      );
  }
}
